import type { PaletteColor } from "@brexhq/metal-foundation";
import styled from "@emotion/styled";
import { padding, margin } from "polished";
import React, { useMemo } from "react";

import { BreakPointVals } from "@brexhq/shared/styles/breakpoints";
import {
  ModuleThemeType,
  ModuleTheme,
  moduleThemes,
  allModuleTheme,
} from "@brexhq/shared/types/ModuleTheme";
import {
  desktop,
  mobile,
  type SpacingValues,
} from "@brexhq/shared/types/SpacingScale";
import type {
  BreakPointVisibility,
  CustomBackgroundProps,
  ModuleLayoutProps,
} from "@brexhq/shared/types/modules/ModuleLayout";

import { useOptimizedBreakpoint } from "../../hooks/useOptimizedBreakpoint";

export const getModuleThemeColors = (
  moduleThemeType: ModuleThemeType,
): ModuleTheme => {
  const validTheme = allModuleTheme.includes(moduleThemeType)
    ? moduleThemeType
    : allModuleTheme[0];
  return moduleThemes[validTheme]; // since "theme" can sometimes be optional, we need a default value.
};

interface OuterModule {
  padTop: SpacingValues;
  mobilePadTop?: SpacingValues;
  padBtm: SpacingValues;
  mobilePadBtm?: SpacingValues;
  margTop: SpacingValues;
  mobileMargTop?: SpacingValues;
  background: PaletteColor;
  customBackground?: CustomBackgroundProps;
  breakpointVisibility: BreakPointVisibility;
  hideOverflowContainerOuter?: boolean;
  removeTopPaddingMobile?: boolean;
  fullHeightContent?: boolean;
}

const ResponsiveContainer = React.memo(
  ({
    targetBreakpoints,
    children,
  }: {
    targetBreakpoints: BreakPointVals[];
    children: React.ReactNode;
  }) => {
    const { shouldRenderOnlyFor } = useOptimizedBreakpoint();

    // Early return if no breakpoints are enabled
    if (targetBreakpoints.length === 0) {
      return null;
    }

    // Use optimized renderOnlyFor function
    if (!shouldRenderOnlyFor(targetBreakpoints)) {
      return null;
    }

    return <>{children}</>;
  },
);

ResponsiveContainer.displayName = "ResponsiveContainer";

const ModuleOuter = styled.section<OuterModule>(
  ({
    theme,
    padTop,
    mobilePadTop = padTop,
    padBtm,
    mobilePadBtm = padBtm,
    margTop,
    mobileMargTop = margTop,
    breakpointVisibility,
    hideOverflowContainerOuter,
    background,
    customBackground,
    removeTopPaddingMobile,
    fullHeightContent,
  }) => {
    const { breakpoints } = theme;

    return {
      ...padding(desktop[padTop], 0, desktop[padBtm], 0),
      ...margin(desktop[margTop], 0, 0, 0),
      width: "100%",
      height: fullHeightContent ? "100%" : "auto",
      background: customBackground ? customBackground?.LRG : background,
      overflow: hideOverflowContainerOuter ? "hidden" : "visible",
      display: breakpointVisibility.showDesktop ? "block" : "none",
      [breakpoints.MED]: {
        display: breakpointVisibility.showTablet ? "block" : "none",
        background: customBackground?.MED,
      },
      [breakpoints.SML]: {
        background: customBackground?.SML,
        display: breakpointVisibility.showMobile ? "block" : "none",
        ...padding(
          removeTopPaddingMobile ? 0 : mobile[mobilePadTop],
          0,
          mobile[mobilePadBtm],
          0,
        ),
        ...margin(mobile[mobileMargTop], 0, 0, 0),
      },
    };
  },
);

const ModuleInner = styled("div")<{
  removeMaxWidth?: boolean;
  fullHeightContent?: boolean;
}>(({ theme, removeMaxWidth, fullHeightContent }) => ({
  maxWidth: removeMaxWidth ? "none" : theme.maxWidths.allContent,
  width: removeMaxWidth ? "100%" : "auto",
  height: fullHeightContent ? "100%" : "auto",
  ...margin(0, "auto"),
}));

export const ModuleLayout = React.forwardRef<HTMLDivElement, ModuleLayoutProps>(
  function ModuleLayoutComponent(
    {
      children,
      theme,
      customBackground,
      bottomPadding,
      mobileBottomPadding,
      topPadding,
      mobileTopPadding,
      topSpacing,
      mobileTopSpacing,
      hideOverflowContainerOuter,
      id,
      anchor,
      isFullWidth,
      style,
      breakpointVisibility,
      responsiveContainer,
      removeTopPaddingMobile,
      removeMaxWidth,
      fullHeightContent,
    },
    ref,
  ) {
    const {
      standard: { background },
    } = getModuleThemeColors(theme);

    const convertSanityBp = (bp?: boolean): boolean => {
      // if value is null from sanity it shows that a user hasn't interacted with the module - in which case we want to show for that option
      if (bp === false) {
        // have to check that it is false so we don't accidentally return on falsy values
        return bp;
      } else return true;
    };

    const targetBreakpoints = useMemo(() => {
      const targets: Array<BreakPointVals> = [];
      if (convertSanityBp(breakpointVisibility?.showMobile))
        targets.push("SML");
      if (convertSanityBp(breakpointVisibility?.showTablet))
        targets.push("MED");
      if (convertSanityBp(breakpointVisibility?.showDesktop))
        targets.push("LRG");
      return targets;
    }, [
      breakpointVisibility?.showMobile,
      breakpointVisibility?.showTablet,
      breakpointVisibility?.showDesktop,
    ]);

    const bps = useMemo(
      () => ({
        showMobile: convertSanityBp(breakpointVisibility?.showMobile),
        showTablet: convertSanityBp(breakpointVisibility?.showTablet),
        showDesktop: convertSanityBp(breakpointVisibility?.showDesktop),
      }),
      [
        breakpointVisibility?.showMobile,
        breakpointVisibility?.showTablet,
        breakpointVisibility?.showDesktop,
      ],
    );

    const outerContent = (
      <ModuleOuter
        ref={ref}
        // Try anchor then default to id
        id={anchor ?? id}
        background={background}
        customBackground={customBackground}
        hideOverflowContainerOuter={hideOverflowContainerOuter}
        margTop={topSpacing}
        mobileMargTop={mobileTopSpacing}
        padTop={topPadding || "none"}
        mobilePadTop={mobileTopPadding}
        padBtm={bottomPadding || "none"}
        mobilePadBtm={mobileBottomPadding}
        breakpointVisibility={bps}
        removeTopPaddingMobile={removeTopPaddingMobile}
        style={style}
        fullHeightContent={fullHeightContent}
      >
        {isFullWidth ? (
          <>{children}</>
        ) : (
          <ModuleInner
            fullHeightContent={fullHeightContent}
            removeMaxWidth={removeMaxWidth}
          >
            {children}
          </ModuleInner>
        )}
      </ModuleOuter>
    );

    if (responsiveContainer) {
      return (
        <ResponsiveContainer targetBreakpoints={targetBreakpoints}>
          {outerContent}
        </ResponsiveContainer>
      );
    }
    return outerContent;
  },
);
